import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import Divider from "@mui/material/Divider";
import useExercises from "providers/ExercisesProvider/useExercises";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";
import useCanDo from "hooks/useCanDo";
import useGetAllCircuitsQuery from "hooks/useGetAllCircuitsQuery";
import { circuitsEnabled } from "config/features";
import CircuitHelpers from "helpers/CircuitHelpers";
import { useEffect, useMemo, useState } from "react";
import { Exercise, ExerciseMetric, Variants, ScoreMetadata, MetadataType, Leaderboard } from "types";
import { Circuit, CircuitMetric } from "types/circuit";
import LeaderboardTable from "../../../components/Leaderboards/LeaderboardTableAbsolute";
import { exerciseAutocompleteFilterOptions, metadataAutocompleteFilterOptions } from "config/autocomplete";
import { TextFieldProps } from "@mui/material";
import { LocalisedLoadingContext } from "components/LocalisedLoadingProvider";
import MessageWithLogo from "components/MessageWithLogo";
import { useLocation } from "react-router-dom";
import logEvent from "utils/logEvent";
import AnalyticsService from "services/AnalyticsService";
import moment from "moment";
import type { DurationInputArg2 } from "moment";
import { defaultPeriod, periods } from "config/periods";
import type { Period } from "config/periods";
import Mui5 from "components/mui5";
import * as Components from "./LiveLeaderboard.components";
import ButtonGroup from "components/ButtonGroup";
import { useTranslation } from "react-i18next";

type MetadataOption =
  | { type: MetadataType.EXERCISE; metadata: Exercise }
  | { type: MetadataType.SCORE; metadata: ScoreMetadata }
  | { type: MetadataType.CIRCUIT; metadata: Circuit };

interface LiveLeaderboardProps {
  initialOptions?: {
    exercise: Exercise | null;
    metric: ExerciseMetric | CircuitMetric | null;
    variant: Variants | null;
    scoreMetadata: ScoreMetadata | null;
    circuit: Circuit | null;
    measureType: "Exercise" | "Score" | "Circuit";
  };
}

function LiveLeaderboard(props: LiveLeaderboardProps) {
  const {
    initialOptions = {
      exercise: null,
      metric: null,
      variant: null,
      scoreMetadata: null,
      circuit: null,
      measureType: "Exercise",
    },
  } = props;
  const exercises = useExercises();
  const hyroxMetadata = useHyroxMetadata();
  const canAccessHyrox = useCanDo("hyrox.any");
  const canAccessCircuits = useCanDo("library.any");
  const { data: circuits } = useGetAllCircuitsQuery({ enabled: circuitsEnabled && canAccessCircuits });
  const loadingState = useState(false);
  const [periodType, setPeriodType] = useState<Period>(defaultPeriod);
  const [{ exercise, metric, variant, scoreMetadata, circuit, measureType }, setOptions] =
    useState<Required<LiveLeaderboardProps>["initialOptions"]>(initialOptions);
  const location = useLocation();
  const { t } = useTranslation();

  // Combine exercises and scores into metadata options
  const metadataOptions = useMemo((): MetadataOption[] => {
    const exerciseOptions: MetadataOption[] = exercises.map((exercise) => ({
      type: MetadataType.EXERCISE,
      metadata: exercise,
    }));

    const scoreOptions: MetadataOption[] = canAccessHyrox
      ? hyroxMetadata
          .filter((score) => score.id === "hyroxConditioning")
          .map((score) => ({
            type: MetadataType.SCORE,
            metadata: score,
          }))
      : [];

    // Only scored circuits (at least one PB-enabled metric) can back a leaderboard
    const circuitOptions: MetadataOption[] = circuits
      .filter((circuit) => CircuitHelpers.getPbEnabledMetrics(circuit).length > 0)
      .map((circuit) => ({
        type: MetadataType.CIRCUIT,
        metadata: circuit,
      }));

    return [...exerciseOptions, ...scoreOptions, ...circuitOptions];
  }, [exercises, hyroxMetadata, canAccessHyrox, circuits]);

  // Get current selected metadata option for autocomplete value
  const selectedMetadataOption = useMemo((): MetadataOption | null => {
    if (measureType === "Score" && scoreMetadata) {
      return { type: MetadataType.SCORE, metadata: scoreMetadata };
    }
    if (measureType === "Circuit" && circuit) {
      return { type: MetadataType.CIRCUIT, metadata: circuit };
    }
    if (measureType === "Exercise" && exercise) {
      return { type: MetadataType.EXERCISE, metadata: exercise };
    }
    return null;
  }, [measureType, exercise, scoreMetadata, circuit]);

  const handlers = useMemo(() => {
    return {
      onMetadataChange: (_: any, option: MetadataOption | null) => {
        if (!option) return;

        if (option.type === MetadataType.SCORE) {
          setOptions((prev) => ({
            ...prev,
            measureType: "Score",
            scoreMetadata: option.metadata as ScoreMetadata,
            exercise: null,
            circuit: null,
            metric: null,
            variant: null,
          }));
        } else if (option.type === MetadataType.CIRCUIT) {
          const circuit = option.metadata as Circuit;
          const pbMetrics = CircuitHelpers.getPbEnabledMetrics(circuit);
          setOptions((prev) => ({
            ...prev,
            measureType: "Circuit",
            circuit,
            exercise: null,
            scoreMetadata: null,
            variant: null,
            metric: pbMetrics.find((m) => m.isDefault) || pbMetrics[0] || null,
          }));
        } else {
          const exercise = option.metadata as Exercise;
          setOptions((prev) => ({
            ...prev,
            measureType: "Exercise",
            exercise,
            scoreMetadata: null,
            circuit: null,
            variant: exercise?.variants.length ? prev.variant || exercise?.variants[0] : null,
            metric: exercise?.metrics.find((m) => m.isDefault) || exercise?.metrics[0] || null,
          }));
        }
      },
      onMetricChange: (_: any, metric: ExerciseMetric | CircuitMetric | null) =>
        setOptions((prev) => ({ ...prev, metric })),
      onVariantChange: (_: any, variant: Variants | null) => setOptions((prev) => ({ ...prev, variant })),
    };
  }, []);

  const endDate = useMemo(() => {
    return moment().endOf("day").toISOString();
  }, []);

  const startDate = useMemo(() => {
    const endOfDay = moment(endDate);

    if (periodType.value === "all") {
      return moment("2019-01-01").toISOString();
    }

    return endOfDay
      .clone()
      .subtract(1, periodType.value as DurationInputArg2)
      .toISOString();
  }, [periodType, endDate]);

  const leaderboard = useMemo((): Leaderboard | null => {
    if (measureType === "Score" && scoreMetadata) {
      return {
        measureType: "Score",
        exerciseId: null,
        metricId: scoreMetadata.field,
        variant: null,
        id: `live-score-${scoreMetadata.id}`,
        name: scoreMetadata.name,
        startTime: startDate,
        endTime: endDate,
        type: "Custom",
        comparePeriodType: null,
        startDateComparePeriod: startDate,
        groups: [],
        groupIds: [],
      };
    }
    if (measureType === "Circuit" && circuit && metric) {
      return {
        measureType: "Circuit",
        exerciseId: circuit.id,
        metricId: metric.field,
        variant: null,
        id: `live-circuit-${circuit.id}`,
        name: circuit.name,
        startTime: startDate,
        endTime: endDate,
        type: "Custom",
        comparePeriodType: null,
        startDateComparePeriod: startDate,
        groups: [],
        groupIds: [],
      };
    }
    return null;
  }, [measureType, scoreMetadata, circuit, metric, startDate, endDate]);

  const leaderboardTableProps = useMemo(() => {
    if (measureType === "Exercise" && exercise && metric) {
      return {
        exercise,
        metric,
        variant,
        startDate,
      };
    }
    if (measureType === "Circuit" && leaderboard && metric) {
      return {
        leaderboard,
        metric,
        startDate,
        endDate,
      };
    }
    if (measureType === "Score" && leaderboard) {
      return {
        leaderboard,
        startDate,
        endDate,
      };
    }
    return null;
  }, [measureType, exercise, metric, variant, leaderboard, startDate, endDate]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const exerciseId = search.get("exerciseId");

    if (!exerciseId) return;

    const foundExercise = exercises.find((e) => e.id === exerciseId);

    if (!foundExercise) return;
    handlers.onMetadataChange(undefined, { type: MetadataType.EXERCISE, metadata: foundExercise });
  }, [exercises, handlers, location.search]);

  const hasVariants = Boolean(exercise?.variants.length);

  // Metric options for the metric selector — exercise metrics or circuit PB-enabled metrics
  const metricSelectorOptions: (ExerciseMetric | CircuitMetric)[] =
    measureType === "Circuit" ? (circuit ? CircuitHelpers.getPbEnabledMetrics(circuit) : []) : exercise?.metrics || [];

  const todayLabel = t("global.time_label_today");
  const pastWeekLabel = t("global.time_label_past_week");
  const pastMonthLabel = t("global.time_label_past_month");
  const allTimeLabel = t("global.time_label_all_time");

  const translatedPeriods = useMemo(() => {
    const labelMap: Record<string, string> = {
      "global.time_label_today": todayLabel,
      "global.time_label_past_week": pastWeekLabel,
      "global.time_label_past_month": pastMonthLabel,
      "global.time_label_all_time": allTimeLabel,
    };
    return periods.map((period) => ({ ...period, label: labelMap[period.label] || period.label }));
  }, [todayLabel, pastWeekLabel, pastMonthLabel, allTimeLabel]);

  return (
    <Box sx={{ height: "100%" }}>
      <Grid
        container
        spacing="0px"
        sx={{ width: "100%", flexDirection: "column", flexWrap: "nowrap", height: "100%", padding: 0 }}
      >
        <Grid>
          <Grid container spacing="0px" sx={{ padding: 0, width: "100%" }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ p: 1, py: 0, pb: 0, pt: 0 }}>
                <Mui5.Autocomplete
                  disableClearable={!!selectedMetadataOption}
                  value={selectedMetadataOption}
                  options={metadataOptions}
                  renderInput={(params: TextFieldProps) => (
                    <Mui5.TextField
                      {...params}
                      margin="dense"
                      size="small"
                      label={t("global.measure_label")}
                      color="primary"
                      fullWidth
                    />
                  )}
                  groupBy={(option: MetadataOption) =>
                    option.type === MetadataType.EXERCISE
                      ? option.metadata.category
                      : option.type === MetadataType.CIRCUIT
                        ? t(`global.metadata_type_${MetadataType.CIRCUIT}`)
                        : t(`global.metadata_type_${MetadataType.SCORE}`)
                  }
                  getOptionLabel={(option: MetadataOption) => option.metadata.name}
                  isOptionEqualToValue={(option, value) =>
                    option.type === value.type && option.metadata.id === value.metadata.id
                  }
                  onChange={(_, option) => {
                    if (option?.type === MetadataType.EXERCISE) {
                      logEvent("HUB_LIVE_SET_LEADERBOARD_EXERCISE", {
                        [AnalyticsService.PROPERTIES.EXERCISE_ID]: option.metadata.id,
                        [AnalyticsService.PROPERTIES.EVENT_CLICK_LOCATION]: "leaderboardExerciseSelector",
                      });
                    }
                    handlers.onMetadataChange(_, option);
                  }}
                  disabled={!metadataOptions.length}
                  filterOptions={metadataAutocompleteFilterOptions}
                />
              </Box>
            </Grid>
            {measureType === "Exercise" && hasVariants && (
              <Grid width={68} textAlign="center">
                <Box sx={{ p: 1, py: 0, pt: 1 }}>
                  <Mui5.ToggleButtonGroup
                    value={variant}
                    onChange={handlers.onVariantChange}
                    exclusive
                    color="primary"
                    size="small"
                  >
                    <Mui5.ToggleButton
                      value="Left"
                      aria-label={t("measures.variant_aria_label_Left")}
                      size="small"
                      sx={{ height: "37px" /* Height of sibling inputs */ }}
                    >
                      {t("measures.variant_single_letter_Left")}
                    </Mui5.ToggleButton>
                    <Mui5.ToggleButton
                      value="Right"
                      aria-label={t("measures.variant_aria_label_Right")}
                      size="small"
                      sx={{ height: "37px" /* Height of sibling inputs */ }}
                    >
                      {t("measures.variant_single_letter_Right")}
                    </Mui5.ToggleButton>
                  </Mui5.ToggleButtonGroup>
                </Box>
              </Grid>
            )}
            {(measureType === "Exercise" || measureType === "Circuit") && (
              <Grid size={{ xs: hasVariants ? "grow" : 12, lg: "grow" }} minWidth={0}>
                <Box sx={{ p: 1, pt: 0 }}>
                  <Mui5.Autocomplete
                    disableClearable={!!metric}
                    value={metric || null}
                    options={metricSelectorOptions}
                    renderInput={(params) => (
                      <Mui5.TextField
                        {...params}
                        margin="dense"
                        size="small"
                        label={t("global.metric_label")}
                        color="primary"
                        fullWidth
                      />
                    )}
                    getOptionLabel={(option) => option.name}
                    onChange={handlers.onMetricChange}
                    disabled={!metricSelectorOptions.length}
                    filterOptions={exerciseAutocompleteFilterOptions}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Components.OverflowGrid flex={1} position="relative">
          <Divider />
          <LocalisedLoadingContext.Provider value={loadingState}>
            {leaderboardTableProps ? (
              <LeaderboardTable {...leaderboardTableProps} />
            ) : (
              <MessageWithLogo>{t("info.select_exercise_and_metric")}</MessageWithLogo>
            )}
          </LocalisedLoadingContext.Provider>
        </Components.OverflowGrid>
        <Divider />
        <Grid>
          <Grid container spacing="0px" sx={{ justifyContent: "center", padding: 0, width: "100%" }}>
            <Grid size={{ xs: 12, lg: 10 }}>
              <Box sx={{ pl: 1, pr: 1 }}>
                <ButtonGroup
                  setOption={(p: Period) => setPeriodType(p)}
                  value={periodType}
                  options={translatedPeriods}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LiveLeaderboard;
